import {
  catchError,
  concatMap,
  exhaustMap,
  map,
  switchMap,
  take,
  tap
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { normalizeToSchema } from 'src/app/shared/normalization/normalization'
import { normalizationActions } from 'src/app/shared/normalization/normalization.actions'
import { ResourceEffects } from 'src/app/shared/resource-effects'

import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { SaleItem } from './sale-item'
import { saleItemActions } from './sale-item.actions'
import * as fromParticipation from './sale-item.reducer'
import { SaleItemService } from './sale-item.service'
import { of } from 'rxjs'
import { persistentUserRoleActions } from '../../role/user-role/persistent-user-role/persistent-user-role.actions'
import { persistentUserRoleSelectors } from '../../role/user-role/persistent-user-role/persistent-user-role.selector'

@Injectable()
export class SaleItemEffects extends ResourceEffects<SaleItem> {
  fetchOneBySaleId = createEffect(() =>
    this.actions$.pipe(
      ofType(saleItemActions.fetchOneBySaleId),
      exhaustMap((action) =>
        this.saleSaleItemService
          .getOneBySaleId(action.saleId, action.saleLine, action.params)
          .pipe(
            map((saleItem) =>
              normalizationActions.upsertMany({
                entities: normalizeToSchema({ saleItems: [saleItem] })
              })
            )
          )
      )
    )
  )
  fetchManyBySaleId = createEffect(() =>
    this.actions$.pipe(
      ofType(saleItemActions.fetchManyBySaleId),
      exhaustMap((action) =>
        this.saleSaleItemService
          .getManyBySaleId(action.saleId, action.params)
          .pipe(
            map((saleItems) =>
              normalizationActions.upsertMany({
                entities: normalizeToSchema({ saleItems })
              })
            )
          )
      )
    )
  )

  updateOneBySaleId = createEffect(() =>
    this.actions$.pipe(
      ofType(saleItemActions.updateOneBySaleId),
      concatMap((action) =>
        this.saleSaleItemService
          .updateOneBySaleId(action.saleId, action.saleLine, action.params)
          .pipe(
            map((saleItem) =>
              normalizationActions.upsertMany({
                entities: normalizeToSchema({ saleItems: [saleItem] })
              })
            ),
            catchError(() => {
              return of(saleItemActions.setLoading({ isLoading: false }))
            })
          )
      )
    )
  )

  updateManyBySaleId = createEffect(() =>
    this.actions$.pipe(
      ofType(saleItemActions.updateManyBySaleId),
      concatMap((action) =>
        this.saleSaleItemService
          .updateManyBySaleId(action.saleId, action.updateParams)
          .pipe(
            map((saleItems) =>
              normalizationActions.upsertMany({
                entities: normalizeToSchema({ saleItems })
              })
            ),
            catchError(() =>
              of(saleItemActions.setLoading({ isLoading: false }))
            )
          )
      )
    )
  )

  createOneById = createEffect(() =>
    this.actions$.pipe(
      ofType(saleItemActions.createOneBySaleId),
      concatMap((action) =>
        this.saleSaleItemService
          .createOneBySaleId(action.saleId, action.params)
          .pipe(
            map((saleItem) =>
              normalizationActions.upsertMany({
                entities: normalizeToSchema({ saleItems: [saleItem] })
              })
            )
          )
      )
    )
  )
  onSetSelectedUserRole = createEffect(
    () =>
      this.actions$.pipe(
        ofType(persistentUserRoleActions.setSelectedUserRole),
        tap((action) => {
          let userRole: any
          this.store
            .select(persistentUserRoleSelectors.selectSelectedUserRole)
            .pipe(take(1))
            .subscribe((user) => {
              userRole = user
            })
          userRole && this.store.dispatch(saleItemActions.resetState())
        })
      ),
    { dispatch: false }
  )
  getAllMany = createEffect(() =>
    this.actions$.pipe(
      ofType(saleItemActions.getAllMany),
      concatMap((action) =>
        this.saleSaleItemService.getAllMany(false, action.params).pipe(
          map((saleItems: any) => {
            this.store.dispatch(
              saleItemActions.actionFinished({
                actionFinished: {
                  action: saleItemActions.getAllMany.type,
                  data: saleItems
                }
              })
            )
            return normalizationActions.upsertMany({
              entities: normalizeToSchema({
                saleItems: saleItems
              })
            })
          })
        )
      )
    )
  )
  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    public saleSaleItemService: SaleItemService,
    public router: Router
  ) {
    super(
      fromParticipation.FEATURE_NAME,
      saleSaleItemService,
      saleItemActions,
      actions$
    )
  }
}
